import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { forgotPassword, resetPassword } from "../../apis/backend";
import "../../App.css";

const ForgotPassword = () => {
    const navigate = useNavigate();

    // Step 1 = Email, Step 2 = OTP + New Password, Step 3 = Success
    const [step, setStep] = useState(1);
    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [successMsg, setSuccessMsg] = useState("");
    const [passwordError, setPasswordError] = useState<string>("");
    const [confirmPasswordError, setConfirmPasswordError] = useState<string>("");
    const [timer, setTimer] = useState<number>(300);
    const [canResend, setCanResend] = useState<boolean>(false);

    // Timer effect for OTP countdown
    React.useEffect(() => {
        let interval: NodeJS.Timeout;
        if (step === 2 && timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => {
                    if (prev <= 1) {
                        setCanResend(true);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [step, timer]);

    // Handle Resend OTP
    const handleResendOtp = async () => {
        try {
            setLoading(true);
            setError("");
            await forgotPassword(email.trim());
            setSuccessMsg("OTP resent to your email.");
            setOtp("");
            setTimer(300);
            setCanResend(false);
        } catch (err: any) {
            setError(err.message || "Failed to resend OTP.");
        } finally {
            setLoading(false);
        }
    };

    // Format seconds as MM:SS
    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    };

    // Password regex: min 8 chars, 1 uppercase, 1 digit
    const passwordRegex = /^(?=.*[A-Z])(?=.*\d).{8,}$/;

    // ---- Step 1: Send OTP ----
    const handleSendOtp = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");

        if (!email.trim()) {
            setError("Please enter your email.");
            return;
        }

        setLoading(true);
        try {
            await forgotPassword(email.trim());
            setSuccessMsg("OTP sent to your email.");
            setTimer(300);
            setCanResend(false);
            setStep(2);
        } catch (err: any) {
            setError(err.message || "Failed to send OTP.");
        } finally {
            setLoading(false);
        }
    };

    // ---- Step 2: Reset Password ----
    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");

        if (!otp.trim()) {
            setError("Please enter the OTP.");
            return;
        }

        if (passwordError || confirmPasswordError) {
            setError("Please fix form errors before submitting.");
            return;
        }

        if (!passwordRegex.test(newPassword)) {
            setError(
                "Password must be at least 8 characters, with at least 1 uppercase letter and 1 digit."
            );
            return;
        }

        if (newPassword !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }

        setLoading(true);
        try {
            await resetPassword(email.trim(), otp.trim(), newPassword);
            setStep(3);
        } catch (err: any) {
            setError(err.message || "Failed to reset password.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="d-flex flex-column justify-content-center align-items-center"
            style={{
                height: "100vh",
                width: "min(30vw, 400px)",
                background: "#ffffff",
            }}
        >
            {/* ======== STEP 1: Email ======== */}
            {step === 1 && (
                <form className="w-100 mw-400 p-4" onSubmit={handleSendOtp}>
                    <h3 className="text-center text-primary fw-bold mb-4">
                        Forgot Password
                    </h3>

                    <p className="text-muted text-center small mb-4">
                        Enter your registered email to receive a One-Time Password (OTP).
                    </p>

                    <div className="mb-3">
                        <input
                            type="email"
                            className="form-control bg-light border-1"
                            placeholder="Enter Email ID"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            style={{ padding: "14px", borderRadius: "10px" }}
                        />
                    </div>

                    {error && (
                        <div className="text-danger text-center mb-3 small">{error}</div>
                    )}

                    <button
                        type="submit"
                        className="btn btn-primary w-100 rounded-3 py-2 fs-6"
                        style={{ background: "#2D88D4" }}
                        disabled={loading}
                    >
                        {loading ? "Sending OTP..." : "Send OTP"}
                    </button>

                    <div className="text-center mt-3">
                        <a href="/" className="text-decoration-none small text-primary">
                            Back to Login
                        </a>
                    </div>
                </form>
            )}

            {/* ======== STEP 2: OTP + New Password ======== */}
            {step === 2 && (
                <form className="w-100 mw-400 p-4" onSubmit={handleResetPassword}>
                    <h3 className="text-center text-primary fw-bold mb-4">
                        Reset Password
                    </h3>

                    {successMsg && (
                        <div className="text-success text-center mb-3 small">
                            {successMsg}
                        </div>
                    )}

                    <div className="mb-3">
                        <input
                            type="text"
                            className="form-control bg-light border-1"
                            placeholder="Enter OTP"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value)}
                            maxLength={6}
                            style={{ padding: "14px", borderRadius: "10px" }}
                        />
                    </div>

                    <div className="mb-3">
                        <input
                            type="password"
                            className="form-control bg-light border-1"
                            placeholder="New Password"
                            value={newPassword}
                            onChange={(e) => {
                                const val = e.target.value;
                                setNewPassword(val);
                                const pwRegex = /^(?=.*[A-Z])(?=.*\d).{8,}$/;
                                if (val && !pwRegex.test(val)) {
                                    setPasswordError("At least 8 characters, 1 uppercase letter, 1 number.");
                                } else {
                                    setPasswordError("");
                                }
                                if (confirmPassword && val !== confirmPassword) {
                                    setConfirmPasswordError("Passwords do not match");
                                } else {
                                    setConfirmPasswordError("");
                                }
                            }}
                            style={{
                                padding: "14px",
                                borderRadius: "10px",
                                borderColor: passwordError ? "red" : "",
                            }}
                        />
                        {passwordError && (
                            <small className="text-danger ps-2">{passwordError}</small>
                        )}
                    </div>

                    <div className="mb-3">
                        <input
                            type="password"
                            className="form-control bg-light border-1"
                            placeholder="Confirm New Password"
                            value={confirmPassword}
                            onChange={(e) => {
                                const val = e.target.value;
                                setConfirmPassword(val);
                                if (newPassword && val !== newPassword) {
                                    setConfirmPasswordError("Passwords do not match");
                                } else {
                                    setConfirmPasswordError("");
                                }
                            }}
                            style={{
                                padding: "14px",
                                borderRadius: "10px",
                                borderColor: confirmPasswordError ? "red" : "",
                            }}
                        />
                        {confirmPasswordError && (
                            <small className="text-danger ps-2">{confirmPasswordError}</small>
                        )}
                    </div>

                    {error && (
                        <div className="text-danger text-center mb-3 small">{error}</div>
                    )}

                    {/* Resend and Timer */}
                    <div className="text-center mb-4">
                        <p className="text-muted small mb-1">
                            Didn't get the code?{" "}
                            <button
                                type="button"
                                onClick={handleResendOtp}
                                disabled={!canResend || loading}
                                className="btn btn-link p-0 text-decoration-none small"
                                style={{
                                    color: canResend ? "#2D88D4" : "#9ca3af",
                                    fontWeight: "600",
                                    cursor: canResend ? "pointer" : "not-allowed",
                                }}
                            >
                                Resend
                            </button>
                        </p>
                        <p className="text-muted small">
                            Expires in{" "}
                            <span className="text-primary fw-bold">
                                {formatTime(timer)}
                            </span>
                        </p>
                    </div>

                    <button
                        type="submit"
                        className="btn btn-primary w-100 rounded-3 py-2 fs-6"
                        style={{ background: timer === 0 ? "#6c757d" : "#2D88D4" }}
                        disabled={loading || timer === 0}
                    >
                        {loading ? "Resetting..." : "Reset Password"}
                    </button>
                </form>
            )}

            {/* ======== STEP 3: Success ======== */}
            {step === 3 && (
                <div className="w-100 mw-400 p-4 text-center">
                    <h3 className="text-primary fw-bold mb-4">Password Reset!</h3>
                    <p className="text-muted mb-4">
                        Your password has been reset successfully. You can now login with
                        your new password.
                    </p>
                    <button
                        className="btn btn-primary w-100 rounded-3 py-2 fs-6"
                        style={{ background: "#2D88D4" }}
                        onClick={() => navigate("/")}
                    >
                        Back to Login
                    </button>
                </div>
            )}
        </div>
    );
};

export default ForgotPassword;
